import type { FC } from "react";
import { Suspense, lazy, useCallback, useMemo, useState } from "react";

import clsx from "clsx";

import { DateTime } from "luxon";

import { useTranslation } from "react-i18next";

import LocationIcon from "assets/icons/filled/location.svg?react";

import { CardWrapper } from "shared/Components";
import EventType from "shared/Components/EventType";
import type { CalendarEventModel } from "shared/types";
import { ImageSizesEnum, Img, MenuDots, type MenuDotsOption, Text } from "shared/uikit";
import { ButtonSizesEnum, TextVariantsEnum } from "shared/uikit/types";

// import RSVPStatus from "../RSVPStatus";

const RemoveSocialEventModal = lazy(() => import("./RemoveSocialEventModal"));

interface SocialEventCardProps {
	event: CalendarEventModel;
	canManage?: boolean;
	onManagedSocialEvent?: () => void;
}

const SocialEventCard: FC<SocialEventCardProps> = ({ event, canManage, onManagedSocialEvent }) => {
	const { t } = useTranslation();

	const [showRemoveModal, setShowRemoveModal] = useState(false);

	const manageOptions = useMemo((): MenuDotsOption[] => {
		if (!canManage) {
			return [];
		}

		return [
			{
				name: t("remove"),
				onClick: () => setShowRemoveModal(true)
			}
		];
	}, [t, canManage]);

	const closeRemoveModal = useCallback(
		(managed?: boolean) => {
			setShowRemoveModal(false);
			if (managed && onManagedSocialEvent) {
				onManagedSocialEvent();
			}
		},
		[onManagedSocialEvent]
	);

	return (
		<>
			<CardWrapper noPadding className={clsx("flex", !event.image ? "flex-col gap-4 p-4" : "items-center")}>
				<div className="flex items-center justify-between">
					<EventType noFloating={!event.image} type={event.type} />
					{canManage && <MenuDots options={manageOptions} bordered horizontal size={ButtonSizesEnum.LG} />}
				</div>
				{event.image && (
					<Img src={event.image} alt={event.name} size={ImageSizesEnum.Small160} className="mr-4 rounded-l-xl" />
				)}
				<div className="flex flex-col gap-2 flex-1">
					<Text variants={TextVariantsEnum.BodySmall}>
						{DateTime.fromJSDate(new Date(event.startsAt)).toFormat("EEE, d MMM h:mm a")}
					</Text>
					<Text variants={TextVariantsEnum.H6}>{event.name}</Text>
					<div className="flex items-center">
						<LocationIcon className="w-4 h-4 mr-1 svg-paths:fill-primary-500" />
						<Text variants={TextVariantsEnum.BodySmall} className="text-hint">
							{event.location?.address}
						</Text>
					</div>
					{/*<RSVPStatus />*/}
				</div>
			</CardWrapper>
			{showRemoveModal && (
				<Suspense>
					<RemoveSocialEventModal eventId={event.id!} onClose={closeRemoveModal} />
				</Suspense>
			)}
		</>
	);
};

export default SocialEventCard;
